import { useState, useEffect } from "react";
import schoolsApi from "../api/schools";

const APP_DOMAINS = ["edupulseai.co.in", "edupulseai.in", "localhost"];

function extractSubdomain() {
  const hostname = window.location.hostname;
  for (const domain of APP_DOMAINS) {
    if (hostname.endsWith(`.${domain}`) || hostname.endsWith(`.${domain}`)) {
      const sub = hostname.replace(`.${domain}`, "");
      if (sub && sub !== "www" && sub !== "app") return sub;
    }
  }
  return null;
}

export function getSubdomain() {
  return extractSubdomain();
}

// Cache for school data with timestamp
const schoolCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

function getCachedSchool(slug) {
  const cached = schoolCache.get(slug);
  if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
    return cached.data;
  }
  return null;
}

function setCachedSchool(slug, data) {
  schoolCache.set(slug, {
    data,
    timestamp: Date.now()
  });
}

export function useSubdomain() {
  const [slug] = useState(() => extractSubdomain());
  const [school, setSchool] = useState(() => {
    // Initialize from cache if available
    if (slug) {
      const cached = getCachedSchool(slug);
      return cached;
    }
    return null;
  });
  const [loading, setLoading] = useState(!!slug && !getCachedSchool(slug));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;

    // Check cache first
    const cached = getCachedSchool(slug);
    if (cached) {
      setSchool(cached);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    schoolsApi
      .getBySlug(slug)
      .then((data) => {
        if (!cancelled) {
          setSchool(data);
          setCachedSchool(slug, data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.status === 404 ? "school_not_found" : "network_error");
          setSchool(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  return { slug, school, loading, error, isSubdomain: !!slug };
}
